import { z } from 'zod';
import { apiGet, apiPost } from '@/lib/api';

const CompartmentSchema = z.object({
  id: z.string(),
  compartmentNo: z.number(),
  /**
   * POZYCJA W KOLUMNIE — dla bębna natywnego równa numerowi SPS, dla DynaBoksa NIE.
   * Siatka układa komórki po TYM polu; `compartmentNo` zostaje do wyświetlenia i adresowania.
   */
  rowNo: z.number(),
  qtyCurrent: z.number().nullable(),
  qtyMax: z.number().nullable(),
  /**
   * SPS `QTYDISP` — ile sztuk zabiera JEDNO wydanie. To jest odpowiedź na towar pakowany
   * po kilka sztuk: „wydaje wszystko" znaczy tu QTYDISP = QTYMAX. `null` = przegródka pusta.
   */
  qtyDisp: z.number().nullable(),
  fillPercent: z.number().nullable(),
  /** SPS `VendingMachineItems.CRITICALLEVEL` dla tego towaru w tym automacie; `null` = nieustawiony. */
  criticalLevel: z.number().nullable(),
  /**
   * ⚠️ LICZONE Z SUMY PO CAŁYM AUTOMACIE, bo próg SPS dotyczy towaru, nie przegródki. Pusta
   * przegródka z przypisanym towarem jest „do uzupełnienia" niezależnie od progu.
   */
  needsRestock: z.boolean(),
  item: z
    .object({
      id: z.string(),
      itemNumber: z.string(),
      name: z.string(),
      unit: z.string(),
      groupId: z.string().nullable(),
      groupName: z.string().nullable(),
    })
    .nullable(),
});

export type Compartment = z.infer<typeof CompartmentSchema>;

const PlanogramSchema = z.object({
  machine: z.object({
    id: z.string(),
    vmid: z.number(),
    name: z.string(),
    // VendingMachines.VMCODE — kolumny CODE w SPS nie ma
    vmCode: z.string().nullable(),
    online: z.boolean(),
    lastSeenAt: z.string().nullable(),
  }),
  summary: z.object({
    drawers: z.number(),
    compartments: z.number(),
    occupied: z.number(),
    empty: z.number(),
    qtyCurrent: z.number(),
    qtyMax: z.number(),
    fillPercent: z.number().nullable(),
    lowCount: z.number(),
    lowThresholdPercent: z.number(),
    /** KOLUMNY bębna — to samo, co `Drawers` w SPS (patrz komentarz niżej). */
    columns: z.number(),
    /** WIERSZE — to, co operator nazywa szufladą; w SPS `DrawerCompartments`. */
    rows: z.number(),
    /** Suma miejsc PO WSZYSTKICH bębnach automatu; rozmiary pojedynczych bębnów są w `drums`. */
    slots: z.number(),
    /** `false` = w sumie siedzi dwuznaczny przeskok, więc to GÓRNE OGRANICZENIE, nie pomiar. */
    slotsCertain: z.boolean(),
    suspiciousColumns: z.array(z.number()),
    /**
     * BĘBNY OSOBNO. Jeden wpis przy zwykłym automacie, dwa przy automacie z dostawką —
     * i wtedy siatek też jest dwie, bo numer kolumny powtarza się między bębnami.
     */
    drums: z.array(
      z.object({
        picNumber: z.number(),
        modeType: z.number().nullable(),
        columns: z.number(),
        /** Wierszy w TYM bębnie — bębny jednej maszyny mogą się różnić wysokością. */
        rows: z.number(),
        /**
         * Gotowa etykieta modułu: „DynaBox 13/54" przy bębnie, a przy szafce
         * „Temrex LockerBox · 15 drzwiczek (2/7/6)". Składana po stronie API.
         */
        label: z.string(),
        /**
         * SZAFKA, NIE BĘBEN — `null` znaczy bęben.
         *
         * ⚠️ Rozstrzyga o tym, czy ma sens walec: karuzela pokazuje, że kolumna 54 sąsiaduje
         * z kolumną 1, a szafka nie jest okrągła — drzwiczki 15 nie stykają się z drzwiczkami 1.
         */
        szafka: z.string().nullable().optional(),
        /**
         * ⚠️ ROZJAZD DRZWICZEK Z PRZEGRÓDKAMI — pokazujemy, nie zamalowujemy. Drzwiczki bez
         * przegródki znaczą, że operator włoży towar tam, gdzie portal go nie widzi.
         */
        rozbieznosciSzafki: z.array(z.string()).optional(),
        slots: z.number(),
        slotsCertain: z.boolean(),
        suspiciousColumns: z.array(z.number()),
      }),
    ),
  }),
  itemGroups: z.array(z.object({ id: z.string(), name: z.string(), compartments: z.number() })),
  /**
   * ⚠️ `drawers` to KOLUMNY siatki, a nie jej wiersze — nazwy pól idą za SPS, gdzie
   * `DRAWERNUMBER` numeruje pionowe wycinki bębna (36), a `COMPARTMENTNUMBER` poziome
   * wiersze (13). Odwrócenie jest udokumentowane w `api/src/planogram/geometry.ts`;
   * przemianowanie modelu rozjechałoby adresowanie config-down po stronie bazy klienta.
   */
  drawers: z.array(
    z.object({
      id: z.string(),
      drawerNo: z.number(),
      /**
       * NUMER BĘBNA (SPS `PICNUMBER`). Bez niego `drawerNo` nie jest jednoznaczny: unikat
       * w bazie to `(drumId, drawerNo)`, więc kolumna „1" istnieje w każdym bębnie.
       */
      picNumber: z.number(),
      /** ile wierszy ma ta kolumna (SPS `COMPARTMENTCOUNT`) */
      compartmentCount: z.number(),
      /** ile MIEJSC bębna zajmuje kolumna: 1, albo 2/3 gdy poszerzona blachą */
      span: z.number(),
      /** szerokość ostatniej kolumny jest ZAŁOŻONA — nie ma za nią czego zmierzyć */
      spanAssumed: z.boolean(),
      /**
       * ⚠️ REGUŁA JEDNOLITEJ ZAWARTOŚCI — cała kolumna jednym towarem, i NIE JEST BIERNA.
       * Zmierzone na DBW2: przy włączonej fladze skasowanie jednej przegródki kasuje wszystkie
       * 13. Ekran ma o tym mówić PRZED kliknięciem.
       */
      uniformContent: z.boolean(),
      /** `false` = instalacja nie przysłała `DynaBoxColumnProperties`, czyli „nie wiem", nie „nie ma". */
      uniformKnown: z.boolean(),
      /** SPS `ENFORCEFULLEMPTYRULE` — wydanie zabiera całą ilość maksymalną. */
      enforceFullEmpty: z.boolean(),
      /** Zasięg powyższej reguły: przy bębnie natywnym kolumna, przy DynaBoksie CAŁY bęben. */
      enforceFullEmptyScope: z.enum(['kolumna', 'beben']),
      compartments: z.array(CompartmentSchema),
    }),
  ),
});

export type Planogram = z.infer<typeof PlanogramSchema>;

export const fetchPlanogram = (groupId: string, machineId: string) =>
  apiGet(`/planogram?groupId=${groupId}&machineId=${machineId}`, PlanogramSchema);

/**
 * PODGLĄD SKUTKÓW przypisania — liczony przez API, nie przez ekran.
 *
 * ⚠️ MUSI BYĆ OSOBNYM WYWOŁANIEM, a nie liczeniem po `data` w kliencie. Podmiana towaru to
 * w SPS DELETE + INSERT, więc stan magazynowy przepada — a ile go przepadnie, wie tylko ta
 * strona, która zaraz złoży zmiany. Policzenie tego drugi raz w kliencie znaczyłoby, że
 * ostrzeżenie i zapis mogą się rozjechać.
 */
const PlanSkutkowSchema = z.object({
  /** ile przegródek dostanie nowy towar (albo zostanie opróżnionych) */
  compartments: z.number(),
  /** ile z nich jest dziś puste — tam nic nie przepada */
  emptyNow: z.number(),
  /** ile ma JUŻ ten sam towar — tam wystarczy zmiana limitów (akcja `P`), bez utraty stanu */
  sameItem: z.number(),
  /** ile ma INNY towar — tam idzie DELETE + INSERT */
  replaced: z.number(),
  /** SUMA SZTUK, która przepadnie przy podmianie. Zero znaczy „nic nie tracisz". */
  stockLost: z.number(),
  /** czy towar trzeba dopisać do automatu (`VendingMachineItems`) — SPS robi to sam, my też musimy */
  addsMachineItem: z.boolean(),
  /** kolumny objęte regułą jednolitej zawartości, które zaznaczenie rusza CZĘŚCIOWO */
  uniformColumnsPartial: z.array(z.number()),
  /**
   * Kolumny, z których operacja ZDEJMIE regułę jednolitej zawartości.
   *
   * ⚠️ To zmiana ustawienia maszyny, o którą nikt wprost nie prosił — wymuszona tym, że po
   * podmianie towaru w części kolumny reguła i tak byłaby fałszywa. Musi paść w potwierdzeniu.
   */
  uniformRulesDropped: z.array(z.number()),
});

export type PlanSkutkow = z.infer<typeof PlanSkutkowSchema>;

export type PrzypiszPayload = {
  groupId: string;
  machineId: string;
  compartmentIds: string[];
  /** `null` = opróżnij zaznaczone przegródki (sam DELETE). */
  itemId: string | null;
  qtyMax?: number;
  /** SPS `QTYDISP` — ile zabiera jedno wydanie; równe `qtyMax` znaczy „wydaje wszystko". */
  qtyDisp?: number;
};

export const podgladPrzypisania = (p: PrzypiszPayload) =>
  apiPost('/planogram/podglad', p, PlanSkutkowSchema);

export const przypiszTowar = (p: PrzypiszPayload) =>
  apiPost('/planogram/przypisz', p, z.object({ opId: z.string(), skutki: PlanSkutkowSchema }));

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════
 *  WYMIANA JEDNEGO INDEKSU NA INNY.
 *
 *  ⚠️⚠️ ZAMÓWIONE 09.09.2026: „wypierdalamy całkiem jeden artykuł i w te same szuflady pakujemy
 *  inny — w tej chwili trzeba to robić ręcznie".
 *
 *  ⚠️⚠️ BEZ `compartmentIds`, I TO JEST CAŁA RÓŻNICA WOBEC `przypiszTowar`. Tam ekran wysyła
 *  zaznaczone przegródki (sufit 200, bo operator je widzi). Tutaj wysyłamy DWA INDEKSY, a przegródki
 *  wylicza serwer z tego, gdzie stary dziś leży — bo ich ręczne zaznaczanie jest tą robotą, której
 *  zgłoszenie każe się pozbyć. Liczenie ich w kliencie znaczyłoby, że ostrzeżenie i zapis mogą się
 *  rozjechać.
 * ══════════════════════════════════════════════════════════════════════════════════════════
 */
const SkutkiPodmianySchema = PlanSkutkowSchema.extend({
  /** Ile przegródek obejmie podmiana — policzone przez serwer, nie przez ekran. */
  przegrodki: z.number(),
  /** Ile zmian pojedzie do bazy klienta (`DELETE` + `INSERT` na przegródkę). */
  zmian: z.number(),
  /** Rozbicie per bęben — okno pokazuje, gdzie stary indeks leży, i pozwala bęben odznaczyć. */
  bebny: z.array(z.object({ picNumber: z.number(), przegrodek: z.number() })),
  zNazwa: z.string(),
  naNazwa: z.string().nullable(),
  /**
   * ⚠️⚠️ ILE LIMITÓW I OGRANICZEŃ ZOSTAJE NA STARYM INDEKSIE. Podmiana ich NIE RUSZA
   * (rozstrzygnięte z użytkownikiem), a limit zostawiony na wycofanym indeksie znaczy nowy towar
   * BEZ ŻADNEGO limitu. Na poziomie TOWARU jest tego mało — zmierzone na czterech bazach
   * klientów: 0, 2, 1 i 0 — ale gdy jest, milczenie ekranu kosztuje.
   */
  limitow: z.number(),
  ograniczen: z.number(),
});

export type SkutkiPodmiany = z.infer<typeof SkutkiPodmianySchema>;

export type PodmianaPayload = {
  groupId: string;
  machineId: string;
  /** Indeks WYCOFYWANY — ten, który fizycznie wyjmujemy z bębna. */
  zItemId: string;
  /**
   * Indeks WSTAWIANY w te same przegródki, albo `null` = OPRÓŻNIĆ je.
   *
   * ⚠️⚠️ `null` zamówione 09.09.2026: „dodajmy jeszcze opcję, by po prostu wyczyścić na puste
   * przegrody". Idzie tą samą trasą co podmiana — serwer przy `null` emituje wyłącznie `DELETE`.
   */
  naItemId: string | null;
  /** Pominięte = wszystkie bębny, w których stary indeks leży. */
  picNumbers?: number[];
  /** Pominięte = pojemność kopiowana per przegródka ze starego indeksu. */
  qtyMax?: number;
  qtyDisp?: number;
};

export const podgladPodmiany = (p: PodmianaPayload) =>
  apiPost('/planogram/podmiana/podglad', p, SkutkiPodmianySchema);

export const podmienIndeks = (p: PodmianaPayload) =>
  apiPost('/planogram/podmiana', p, SkutkiPodmianySchema.extend({ opId: z.string() }));

/**
 * PRZENIESIENIE LIMITÓW I OGRANICZEŃ Z WYCOFANEGO INDEKSU NA NOWY.
 *
 * ⚠️⚠️ OSOBNA OPERACJA I OSOBNA DECYZJA, rozstrzygnięte z użytkownikiem: podmiana w planogramie
 * dotyka towaru w przegródkach, a TO dotyka UPRAWNIEŃ LUDZI (`Rationing`, `Restrictions2`).
 *
 * ⚠️⚠️ BEZ `machineId`, I EKRAN MUSI TO POWIEDZIEĆ. Te tabele nie mają numeru automatu — limit
 * „6 par rękawic na miesiąc" dotyczy człowieka, nie maszyny. Przycisk stoi w oknie JEDNEGO
 * automatu, więc bez tego zdania wyglądałby na operację o zasięgu tego automatu.
 *
 * ⚠️ KOLIZJA WRACA JAKO BŁĄD Z TREŚCIĄ. Gdy nowy indeks ma własny wpis dla tego samego celu, serwer
 * odmawia CAŁOŚCI i wymienia cele — przeniesienie połowiczne uprawnień jest gorsze niż jego brak.
 */
export const przeniesLimity = (p: { groupId: string; zItemId: string; naItemId: string }) =>
  apiPost(
    '/planogram/podmiana/limity',
    p,
    z.object({ opId: z.string(), limitow: z.number(), ograniczen: z.number(), zmian: z.number() }),
  );

/**
 * WIERSZE, KTÓRE PORTAL ZAŁOŻYŁ NIEPEŁNE — sprzątanie po własnej awarii.
 *
 * ⚠️ NIE JEST TO FUNKCJA PLANOGRAMU, tylko naprawa skutku błędu, który wywalał SPS: nasz `INSERT`
 * zostawiał puste kolumny, których program nigdy nie zostawia pustych, i po takim wierszu
 * przestawał otwierać bęben. Portal pokazywał maszynę dalej normalnie, bo jego czytnik jest
 * luźniejszy — dlatego pasek z tą liczbą jest jedynym miejscem, w którym to widać.
 *
 * Licznik schodzi do zera sam: naprawiony wiersz przestaje być kandydatem, więc pasek znika.
 */
export const podgladNaprawy = (groupId: string, machineId: string) =>
  apiGet(
    `/planogram/naprawa?groupId=${groupId}&machineId=${machineId}`,
    z.object({ doNaprawy: z.number(), pominiete: z.array(z.string()) }),
  );

export const naprawWiersze = (groupId: string, machineId: string) =>
  apiPost(
    '/planogram/napraw-wiersze-towarow',
    { groupId, machineId },
    z.object({ opId: z.string(), naprawionych: z.number(), pominiete: z.array(z.string()) }),
  );

export type RegulyKolumnyPayload = {
  groupId: string;
  machineId: string;
  picNumber: number;
  columnNo: number;
  /** Pominięte = nie ruszamy tej reguły. */
  uniformContent?: boolean;
  enforceFullEmpty?: boolean;
};

/**
 * Reguły kolumny bębna.
 *
 * ⚠️ ZASIĘG WRACA Z API, a nie jest zakładany w kliencie: `ENFORCEFULLEMPTYRULE` mieszka
 * w `Drawers`, a DynaBox ma jeden wiersz `Drawers` na CAŁY bęben — więc tam zmiana obejmuje
 * wszystkie kolumny, nie tę jedną.
 */
export const ustawRegulyKolumny = (p: RegulyKolumnyPayload) =>
  apiPost(
    '/planogram/reguly-kolumny',
    p,
    z.object({ opId: z.string(), dynabox: z.boolean(), zasiegPelnegoWydania: z.enum(['kolumna', 'beben']) }),
  );

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  SZEROKOŚĆ KOLUMN BĘBNA — układ sekcji i to, co wolno z nimi zrobić.
 *
 *  ⚠️⚠️ SKUTEK PRZYCHODZI RAZEM Z PROPOZYCJĄ, a nie po wyborze kolumny. Poszerzenie kasuje cały
 *  pion przegród razem z towarem, więc liczba musi być na ekranie ZANIM człowiek wybierze, którą
 *  kolumnę złączyć — dopytanie po kliknięciu przychodzi o krok za późno.
 *
 *  ⚠️ `spojna: false` ZNACZY „NIE UMIEMY NARYSOWAĆ TEJ SEKCJI" i nic w niej nie proponujemy.
 *  To jest inny stan niż „nie da się nic złączyć" i ekran musi je rozróżnić.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const SkutekSchema = z.object({
  przegrodek: z.number(),
  pozycji: z.number(),
  pozycjiZeStanem: z.number(),
  sztuk: z.number(),
});

export const PropozycjaSchema = z.object({
  sekcja: z.number(),
  zostaje: z.number(),
  kolumna: z.number(),
  skutek: SkutekSchema,
  poziom: z.enum(['BEZ_SKUTKU', 'ZNIKNIE_PLANOGRAM', 'GINIE_TOWAR']),
});

export type Propozycja = z.infer<typeof PropozycjaSchema>;

export const UkladKolumnSchema = z.object({
  uklad: z.enum(['ROTO', 'DYNABOX']),
  kolumnFizycznych: z.number(),
  wSekcji: z.number(),
  poziomow: z.number(),
  sekcje: z.array(
    z.object({
      nr: z.number(),
      fizyczne: z.array(z.number()),
      spojna: z.boolean(),
      kolumny: z.array(
        z.object({
          numer: z.number(),
          szerokosc: z.number(),
          fizyczne: z.array(z.number()),
          pozycji: z.number(),
          sztuk: z.number(),
        }),
      ),
    }),
  ),
  poszerzenia: z.array(PropozycjaSchema),
  zwezenia: z.array(PropozycjaSchema),
  niespojne: z.array(z.number()),
});

export type UkladKolumn = z.infer<typeof UkladKolumnSchema>;

export const fetchKolumnyBebna = (groupId: string, vmid: number, picNumber: number) =>
  apiGet(
    `/planogram/beben/kolumny?groupId=${encodeURIComponent(groupId)}&vmid=${vmid}&picNumber=${picNumber}`,
    UkladKolumnSchema,
  );

export const poszerzKolumne = (p: { groupId: string; vmid: number; picNumber: number; kolumna: number }) =>
  apiPost('/planogram/beben/poszerz', p, z.object({ opId: z.string(), skutek: SkutekSchema }));

export const zwezKolumne = (p: { groupId: string; vmid: number; picNumber: number; kolumna: number }) =>
  apiPost('/planogram/beben/zwez', p, z.object({ opId: z.string(), odzyskiwana: z.number() }));
